import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Cpu, HardDrive, X } from 'lucide-react'
import { Product } from '../../types'
import OptimizedImage from '../Image/OptimizedImage'
import { emailUtils } from '../../utils/contactUtils'

interface ProductCardProps {
  product: Product
  showDetails?: boolean
}

const ProductCard: React.FC<ProductCardProps> = ({ 
  product, 
  showDetails = false
}) => {
  const { t } = useTranslation()
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const [isImageHovered, setIsImageHovered] = useState(false)
  const categoryIcon = product.category === 'software' ? Cpu : HardDrive
  const CategoryIcon = categoryIcon
  
  // 获取翻译的产品信息
  const getTranslatedProductInfo = (productId: string) => {
    const productTranslations = t(`products.${productId}`, { returnObjects: true }) as any
    if (productTranslations && typeof productTranslations === 'object') {
      return productTranslations
    }
    // 如果没有翻译，返回原始产品信息
    return {
      name: product.name,
      description: product.description,
      features: product.features
    }
  }
  
  const translatedProduct = getTranslatedProductInfo(product.id)

  // 处理ESC键关闭模态框
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isImageModalOpen) {
        setIsImageModalOpen(false)
      }
    }

    if (isImageModalOpen) {
      document.addEventListener('keydown', handleEscape)
      // 防止背景滚动
      document.body.style.overflow = 'hidden'
      // 重置hover状态，避免闪烁
      setIsImageHovered(false)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isImageModalOpen])

  return (
    <div className={`bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 flex flex-col h-full ${
      !isImageModalOpen ? 'hover:shadow-xl hover:-translate-y-1' : ''
    }`}>
      {/* Product Image - 增大图片区域 */}
      <div 
        className={`h-72 w-full relative cursor-pointer transition-all duration-200 ${
          isImageHovered && !isImageModalOpen ? 'bg-gray-100' : 'bg-gray-50'
        }`}
        onClick={() => setIsImageModalOpen(true)}
        onMouseEnter={() => !isImageModalOpen && setIsImageHovered(true)}
        onMouseLeave={() => setIsImageHovered(false)}
      >
        <OptimizedImage
          src={product.image || ''}
          alt={translatedProduct.name || product.name}
          className={`h-full w-full transition-transform duration-200 ${
            isImageHovered && !isImageModalOpen ? 'scale-105' : 'scale-100'
          }`}
          objectFit="contain"
        />
        {/* 放大提示图标 */}
        <div 
          className={`absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded-full transition-opacity duration-200 pointer-events-none ${
            isImageHovered && !isImageModalOpen ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
            <path d="M11 8v6" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 11h6" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </div>
      </div>

      {/* Product Info - 使用flex-1让内容区域自适应 */}
      <div className="p-6 flex flex-col flex-1">
        {/* Category Badge */}
        <div className="flex items-center justify-between mb-3">
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${
            product.category === 'software' 
              ? 'bg-blue-100 text-blue-800' 
              : 'bg-green-100 text-green-800'
          }`}>
            <CategoryIcon size={12} className="inline mr-1" />
            {product.category === 'software' ? t('pages.products.categories.software') : t('pages.products.categories.hardware')}
          </span>
        </div>

        {/* Product Name */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 leading-tight">
          {translatedProduct.name || product.name}
        </h3>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed flex-1">
          {translatedProduct.description || product.description}
        </p>

        {/* Features Preview */}
        {(translatedProduct.features || product.features) && (translatedProduct.features || product.features).length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">{t('pages.products.actions.keyFeatures')}</h4>
            <ul className="space-y-1">
              {(translatedProduct.features || product.features).slice(0, 3).map((feature: string, index: number) => (
                <li key={index} className="text-xs text-gray-600 flex items-start">
                  <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                  <span className="leading-relaxed">{feature}</span>
                </li>
              ))}
              {(translatedProduct.features || product.features).length > 3 && (
                <li className="text-xs text-gray-500 italic">
                  +{(translatedProduct.features || product.features).length - 3} {t('pages.products.moreFeatures')}
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Specifications Preview */}
        {product.specifications && showDetails && (
          <div className="mb-4 bg-gray-50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Specifications:</h4>
            <div className="space-y-1">
              {Object.entries(product.specifications).slice(0, 2).map(([key, value]) => (
                <div key={key} className="flex justify-between text-xs">
                  <span className="text-gray-600">{key}:</span>
                  <span className="text-gray-900 font-medium">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Button - 固定在底部 */}
        <div className="flex justify-center mt-auto pt-2">
          <button
            className="inline-flex items-center text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors duration-200 hover:bg-blue-50 px-3 py-2 rounded-lg"
            onClick={() => emailUtils.openProductInquiryEmail(
              t, 
              translatedProduct.name || product.name, 
              translatedProduct.description || product.description
            )}
          >
            {t('pages.products.actions.learnMore')}
            <ArrowRight size={16} className="ml-1" />
          </button>
        </div>
      </div>

      {/* 图片放大模态框 */}
      {isImageModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
          onClick={() => {
            setIsImageModalOpen(false)
            setIsImageHovered(false)
          }}
          onMouseMove={() => setIsImageHovered(false)}
        >
          {/* 关闭按钮 */}
          <button
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors duration-200 z-20 bg-black bg-opacity-50 rounded-full p-2"
            onClick={() => {
              setIsImageModalOpen(false)
              setIsImageHovered(false)
            }}
          >
            <X size={24} />
          </button>
          
          {/* 产品信息栏 */}
          <div className="absolute top-6 left-6 z-20 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg">
            <h3 className="text-lg font-semibold">
              {translatedProduct.name || product.name}
            </h3>
          </div>
          
          {/* 放大的图片容器 */}
          <div 
            className="relative w-full h-full flex items-center justify-center p-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 白色背景的图片展示区域 */}
            <div className="relative bg-white rounded-xl shadow-2xl p-8 max-w-5xl max-h-[85vh] flex items-center justify-center">
              {/* 放大的图片 */}
              <div style={{ maxWidth: '80vw', maxHeight: '70vh' }} className="flex items-center justify-center">
                <OptimizedImage
                  src={product.image || ''}
                  alt={translatedProduct.name || product.name}
                  className="max-w-full max-h-full"
                  objectFit="contain"
                />
              </div>
            </div>
          </div>
          
          {/* 底部提示信息 */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg text-sm">
            {t('pages.products.imageModal.closeHint')}
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductCard